import type { Session } from '@remix-run/node';
import _ from 'lodash';
import type { Variant } from '~/services/api-utils.server';
import type {
  Address,
  BillingAddress,
  Cart,
  Recipient,
  ShippingInventoryPart,
  ShippingInventoryProduct,
  StoragePrices,
} from '~/services/api.server';
import { scApi } from '~/services/api.server';
import { typedBoolean } from './common';
import { getPageParamValue } from './searchParams';
import { getWeightInPounds } from './shipping';

type Shipment = {
  id?: string;
  parts: Array<
    ShippingInventoryPart & { production_time: number; weight: string; weight_unit: { id: string; name: string } }
  >;
  partsQty: { [key: ShippingInventoryProduct['parts'][number]['id']]: number };
  recipients: Recipient[];
  recipientsShipping: {
    [key: Recipient['id']]: {
      selected: 'standard' | 'express';
      shippingDate: string;
    };
  };
  maxRecipients: number;
  weight: number;
  production_time: number;
};

export type SendSwagData = Shipment & {
  redirectTo?: string;
  created?: true;
  billing_address?: Address;
  shipments: Array<{ id: string } & Shipment>;
  cart?: Cart;
};

export type SendSwagProduct = ShippingInventoryProduct & { production_time: number };

type RemoveStoragePricePrefix<T> = T extends `STORAGE_PRICE_${infer U}` ? U : never;
export type WarehouseInfo = Partial<Record<RemoveStoragePricePrefix<StoragePrices[number]['key']>, number>>;

type AvailableAttributesMetadata = {
  [key: string]: {
    part: ShippingInventoryProduct['parts'][number]['id'];
    unavailableAttributes: { [key in keyof Variant]: string[] };
  };
};

function getPermutations(attributes: ShippingInventoryProduct['available_attributes']) {
  if (!attributes) {
    return [];
  }

  const permutations = [];
  const keys = Object.keys(attributes);
  const keySizes = keys.map((key) => attributes[key as keyof typeof attributes]?.length).filter(typedBoolean);
  const size = keySizes.reduce((acc, cur) => acc * cur, 1);
  let i = 0;

  while (i < size) {
    const perm: { [k: string]: { name: string } } = {};
    let j = i;
    keys.forEach((key, idx) => {
      const attr = attributes[key as keyof typeof attributes]?.[j % keySizes[idx]];
      if (attr) {
        perm[key] = attr;
      }
      j = Math.floor(j / keySizes[idx]);
    });
    permutations.push(perm);
    i++;
  }

  return permutations;
}

export const getMetadataKey = (attributes: Record<string, string | { name: string }>) =>
  Object.keys(attributes)
    .sort()
    .map((k) => {
      const value = attributes[k];
      return typeof value === 'object' ? value.name : value;
    })
    .join('-');

export function getAvailableAttributesMetadata(product: ShippingInventoryProduct): AvailableAttributesMetadata {
  const allPermutations = getPermutations(product.available_attributes);

  const [availables, unavailables] = _.partition(allPermutations, (permutation) =>
    product.parts.find((part) =>
      Object.keys(permutation).every((key) => {
        const singularKey = key.toString().slice(0, -1) as keyof Variant;
        return permutation[key]?.name === part.variant[singularKey]?.name;
      })
    )
  );

  return availables.reduce((acc, permutation) => {
    const part = product.parts.find((part) =>
      Object.keys(permutation).every((key) => {
        const singularKey = key.toString().slice(0, -1) as keyof Variant;
        return permutation[key]?.name === part.variant[singularKey]?.name;
      })
    );

    if (part) {
      const keys = Object.keys(permutation);
      const unavailableAttributes = keys.map((key) => {
        const singularKey = key.toString().slice(0, -1) as keyof Variant;
        const restKeys = keys.filter((k) => k !== key);

        return [
          singularKey,
          unavailables
            .filter((unavailable) =>
              restKeys.every((restKey) => unavailable[restKey].name === permutation[restKey].name)
            )
            .map((unavailable) => unavailable[key].name),
        ];
      });

      acc[getMetadataKey(permutation)] = {
        part: part.id,
        unavailableAttributes: Object.fromEntries(unavailableAttributes),
      };
    }

    return acc;
  }, {} as AvailableAttributesMetadata);
}

export const productsPerPage = 12;

export function getInventoryForShipping(searchParams: URLSearchParams, accessToken: string, accountId: number) {
  const page = getPageParamValue(searchParams);
  const offset = ((page - 1) * productsPerPage).toString();

  const query = {
    limit: productsPerPage.toString(),
    offset,
  };

  return scApi.getInventoryForShipping(accessToken, accountId, query);
}

export function getSendSwagData(url: string): { sendSwagData: SendSwagData; key: string };
export function getSendSwagData(url: string, session: Session): { sendSwagData: SendSwagData; key: string };
export function getSendSwagData(url: string, session?: Session): { sendSwagData: SendSwagData; key: string } {
  const initialSendSwagData: SendSwagData = {
    parts: [],
    partsQty: {},
    recipients: [],
    recipientsShipping: {},
    maxRecipients: 0,
    weight: 0,
    shipments: [],
    production_time: 0,
  };

  let from;
  const _url = new URL(url);
  if (_url.pathname.includes('checkout')) {
    from = 'from-cart';
  } else if (url.includes('from-draft')) {
    const draftId = _url.pathname.split('from-draft/')[1].split('/')[0];
    from = 'from-draft-' + draftId;
  } else {
    from = _url.pathname.split('/').find((part) => part.startsWith('from-'));
  }
  const key = `sendSwag-${from}`;

  if (!session) {
    return { sendSwagData: initialSendSwagData, key };
  }

  const currentSendSwagData = session.get(key) as SendSwagData;

  const sendSwagData =
    !currentSendSwagData || (currentSendSwagData.created && !_url.pathname.includes('/confirmation'))
      ? initialSendSwagData
      : currentSendSwagData;

  return { sendSwagData, key };
}

export const getProductWeight = (weight: number, unit: string) => {
  return Math.ceil(getWeightInPounds(isNaN(weight) ? 0 : weight, unit));
};

export function convertBillingAddressToAddress(billingAddress: BillingAddress): Address {
  return {
    first_name: billingAddress.first_name,
    last_name: billingAddress.last_name,
    email: billingAddress.email,
    phone: billingAddress.phone,
    company: billingAddress.company,
    line_1: billingAddress.address.line_1,
    line_2: billingAddress.address.line_2,
    city: billingAddress.address.city,
    state: billingAddress.address.state,
    postal_code: billingAddress.address.postal_code,
    country: billingAddress.address.country,
  };
}

export function getWarehouseInfo(sendSwagData: SendSwagData) {
  const warehouseInfo: WarehouseInfo = {};

  const visitedParts = {} as Record<number, boolean>;
  for (const shipment of sendSwagData.shipments) {
    const { parts, partsQty } = shipment;
    for (const part of parts) {
      const partType = part.items.length > 0 ? 'PACK' : 'PRODUCT';
      const key: keyof WarehouseInfo = `${partType}_${part.storage_category.id}`;

      const quantity = partsQty[part.id] * shipment.recipients.length;
      if (!visitedParts[part.id]) {
        warehouseInfo[key] = (warehouseInfo[key] ?? 0) + part.stock - quantity;
        visitedParts[part.id] = true;
      } else {
        warehouseInfo[key] = warehouseInfo[key]! - quantity;
      }
    }
  }

  return warehouseInfo;
}

export function getWarehouseTotalPrice(warehouseInfo: WarehouseInfo) {
  const storagePrices = JSON.parse(localStorage.getItem('storage-prices') || '[]') as StoragePrices;

  return Object.entries(warehouseInfo).reduce((acc, [key, qty]) => {
    const price = storagePrices.find((p) => p.key.endsWith(key))?.price.amount ?? 0;
    return acc + price * qty;
  }, 0);
}
